import { Model, ModelsFilterParams, NamedModel } from 'api/Exec/types';
import TableTopPanel from 'app/common_components/TableTopPanel/TableTopPanel';
import usePaginationParams from 'app/hooks/usePaginationParams';
import DetailedDockerImage from 'components/Dashboard/Models/DockerImages/DetailedDockerImage/DetailedDockerImage';
import DockerImageForm from 'components/Dashboard/Models/DockerImages/DockerImageForm/DockerImageForm';
import DockerImagesFilter from 'components/Dashboard/Models/DockerImages/DockerImagesFilter/DockerImagesFilter';
import useModels from 'components/Dashboard/Models/useModels';
import { DashboardPageProps } from 'components/Dashboard/types';
import useColumns from 'components/Dashboard/Models/DockerImages/hooks';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Typography } from 'antd';

interface Props extends Partial<DashboardPageProps> {
  addingNamedModel?: boolean;
  selectRow?: (model: Model) => void;
  selectedNamedModel?: NamedModel;
}

const DockerImages: React.FC<Props> = ({ setTitle, addingNamedModel, selectRow, selectedNamedModel }) => {
  const initialModelSelected = useRef<boolean>(false);
  const { isLoading, models, fetchModels, setModels } = useModels();
  const {
    searchParam,
    modelParam,
    modelApiParam,
    namedModelsParam,
    isSandboxParam,
    isCvParam,
    pushDateParam,
  } = usePaginationParams();

  const getFilter = useCallback((): ModelsFilterParams => ({
    search: searchParam,
    model: modelParam,
    modelApiNames: selectedNamedModel ? [ selectedNamedModel.modelApiName ] : modelApiParam.split(','),
    namedModels: namedModelsParam.split(','),
    isSandbox: isSandboxParam,
    isCv: isCvParam,
    pushDate: pushDateParam,
  }), [
    searchParam,
    modelParam,
    modelApiParam,
    namedModelsParam,
    isSandboxParam,
    isCvParam,
    pushDateParam,
    selectedNamedModel,
  ]);

  useEffect(() => {
    const abortController = new AbortController();
    fetchModels(getFilter(), abortController);

    return () => {
      abortController.abort();
    };
  }, [ fetchModels, getFilter ]);

  useEffect(() => {
    if (selectedNamedModel && selectRow && !initialModelSelected.current) {
      const model = models.find(model => model.id === selectedNamedModel.modelId);
      if (model) {
        selectRow(model);
        initialModelSelected.current = true;
      }
    }
  }, [ models, selectRow, selectedNamedModel ]);

  const [ isDetailedModelOpen, setIsDetailedModelOpen ] = useState<Model | null>(null);
  const openDetailedModel = (model: Model) => setIsDetailedModelOpen(model);
  const closeDetailedModel = () => setIsDetailedModelOpen(null);

  const [ isDockerImageModalOpen, setIsDockerImageModalOpen ] = useState<boolean>(false);
  const openDockerImageModal = () => setIsDockerImageModalOpen(true);
  const closeDockerImageModal = () => setIsDockerImageModalOpen(false);

  const addDockerImageAndCloseModal = (model: Model) => {
    setModels((prevState: Model[]) => [ model, ...prevState ]);
    closeDockerImageModal();
  };

  const { columns } = useColumns(openDetailedModel, addingNamedModel);

  useEffect(() => {
    if (setTitle) {
      setTitle('Docker images');
    }
  }, [ setTitle ]);

  return (
    <>
      <TableTopPanel
        hideModelApi={!!selectedNamedModel?.modelApiName}
        Filter={DockerImagesFilter}
        RightColumn={!addingNamedModel ? <Button type="primary" onClick={openDockerImageModal} icon={<PlusOutlined />}>Add docker image</Button> : undefined}
        placeholder="Search by image tag"
      />

      <Table
        rowKey={record => record.id}
        columns={columns}
        dataSource={models}
        pagination={addingNamedModel ? undefined : false}
        scroll={{ x: true }}
        loading={isLoading}
        rowSelection={addingNamedModel ? {
          type: 'radio',
          onSelect: model => selectRow && selectRow(model),
          defaultSelectedRowKeys: selectedNamedModel ? [ selectedNamedModel.modelId ] : undefined,
          getCheckboxProps: () => ({ disabled: selectedNamedModel?.clinicalValidated }),
        } : undefined}
      />

      <Modal
        title={<Typography.Title level={4}>Add docker image</Typography.Title>}
        open={isDockerImageModalOpen}
        footer={null}
        width={1280}
        onCancel={closeDockerImageModal}
      >
        <DockerImageForm onCancel={closeDockerImageModal} onDockerImageAdded={addDockerImageAndCloseModal} />
      </Modal>

      <Modal
        title={<Typography.Title level={4}>Docker image details</Typography.Title>}
        open={!!isDetailedModelOpen}
        footer={null}
        width={1000}
        onCancel={closeDetailedModel}
      >
        <DetailedDockerImage model={isDetailedModelOpen} />
      </Modal>
    </>
  );
};

export default DockerImages;
